import { useState } from 'react';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid2';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import { PageSection, useToast } from '@radiology/ui';
import {
  COLOR_LABEL,
  useDeviceAttributes,
  useSaveDevice,
  type Device,
  type DeviceInput,
} from './api';

const EMPTY: DeviceInput = {
  equipment_name: '',
  model: '',
  serial_no: '',
  manufacturer: '',
  htm: '',
  kn: '',
  md: '',
  po_no: '',
  site_name: '',
  location: '',
  section: '',
  capital_equity_information: '',
  color_category: '',
  installation_date: '',
  obsolete: 'No',
  qc_performed_by: '',
  last_qc_date: '',
  next_qc_date: '',
  pm_performed_by: '',
  last_pm_date: '',
  next_pm_date: '',
};

function fromDevice(d: Device): DeviceInput {
  return {
    equipment_name: d.equipment_name,
    model: d.model ?? '',
    serial_no: d.serial_no ?? '',
    manufacturer: d.manufacturer ?? '',
    htm: d.htm ?? '',
    kn: d.kn ?? '',
    md: d.md ?? '',
    po_no: d.po_no ?? '',
    site_name: d.site_name ?? '',
    location: d.location ?? '',
    section: d.section ?? '',
    capital_equity_information: d.capital_equity_information ?? '',
    color_category: d.color_category ?? '',
    installation_date: d.installation_date ?? '',
    obsolete: d.is_obsolete ? 'Yes' : 'No',
    qc_performed_by: d.qc_performed_by ?? '',
    last_qc_date: d.last_qc_date ?? '',
    next_qc_date: d.next_qc_date ?? '',
    pm_performed_by: d.pm_performed_by ?? '',
    last_pm_date: d.last_pm_date ?? '',
    next_pm_date: d.next_pm_date ?? '',
  };
}

/** QC-BR-04 / PM-BR-04 — the default next date is one year on. */
function oneYearOn(date: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return '';
  const d = new Date(date + 'T00:00:00Z');
  d.setUTCFullYear(d.getUTCFullYear() + 1);
  return d.toISOString().slice(0, 10);
}

/**
 * Add or edit a device.
 *
 * Site, section and location come from `core.attributes` — the old `Settings`
 * singleton, whose ten array columns were unnested into 100 rows during the
 * merge. They are free-text-with-suggestions rather than strict dropdowns,
 * because the migrated data contains values that are not in the lists (`section`
 * has 48 configured values but the devices use names like "Others (Portable
 * X-Ray & C-Arm)"), and a strict dropdown would make those rows uneditable.
 *
 * Note what is NOT here: `years_in_services`. The legacy form asked the user to
 * type it, which is how it came to disagree with the installation date on 69 of
 * 154 rows. It is computed on read now.
 */
export function DeviceForm({ device, onSaved }: { device?: Device; onSaved?: () => void }) {
  const save = useSaveDevice();
  const toast = useToast();
  const [form, setForm] = useState<DeviceInput>(device ? fromDevice(device) : EMPTY);
  const [error, setError] = useState<string | null>(null);

  const sites = useDeviceAttributes('site');
  const sections = useDeviceAttributes('section');
  const locations = useDeviceAttributes('location');

  const set = <K extends keyof DeviceInput>(key: K, value: DeviceInput[K]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  /**
   * Changing the LAST date refreshes the next one (QC-BR-06 / PM-BR-06).
   *
   * Mirrors the database trigger so the secretary sees the new date immediately
   * rather than after saving. The trigger remains the authority — if they then
   * edit the next date by hand, that value is what is stored and what drives
   * status and reminders (QC-BR-07).
   */
  const setLastDate = (which: 'qc' | 'pm', value: string) =>
    setForm(prev => ({
      ...prev,
      [`last_${which}_date`]: value,
      [`next_${which}_date`]: oneYearOn(value),
    }));

  const submit = () => {
    if (!form.equipment_name.trim()) {
      setError('Equipment name is required.');
      return;
    }
    if (form.installation_date && !/^\d{4}-\d{2}-\d{2}$/.test(form.installation_date)) {
      setError('Installation date must be a real date.');
      return;
    }

    setError(null);
    save.mutate(
      { ...form, id: device?.id },
      {
        onSuccess: () => {
          toast.success(device ? 'Device updated' : 'Device added to the register');
          if (!device) setForm(EMPTY);
          onSaved?.();
        },
        onError: (e: Error) => setError(e.message),
      },
    );
  };

  // Narrowed to the three free-text fields it is actually used for, rather than
  // `keyof DeviceInput` — that would include `obsolete`, whose value is a
  // two-value union and not free text.
  const suggest = (
    label: string,
    key: 'site_name' | 'section' | 'location',
    options: string[] | undefined,
    size: number,
  ) => (
    <Grid size={{ xs: 12, sm: 6, md: size }}>
      <Autocomplete
        freeSolo
        options={options ?? []}
        value={form[key] ?? ''}
        onInputChange={(_e, value) => set(key, value)}
        renderInput={params => <TextField {...params} label={label} fullWidth />}
      />
    </Grid>
  );

  return (
    <PageSection
      title={device ? 'Edit device' : 'Add a device'}
      description="Only the equipment name is required — the legacy register is sparse and rejecting incomplete rows would make most of it uneditable."
    >
      <Stack gap={2}>
        {error && <Alert severity="error">{error}</Alert>}

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="Equipment name"
              fullWidth
              required
              value={form.equipment_name}
              onChange={e => set('equipment_name', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              label="Manufacturer"
              fullWidth
              value={form.manufacturer}
              onChange={e => set('manufacturer', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              label="Model"
              fullWidth
              value={form.model}
              onChange={e => set('model', e.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              label="Serial number"
              fullWidth
              value={form.serial_no}
              onChange={e => set('serial_no', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              label="HTM"
              fullWidth
              value={form.htm}
              onChange={e => set('htm', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              label="KN"
              fullWidth
              value={form.kn}
              onChange={e => set('kn', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              label="MD"
              fullWidth
              value={form.md}
              onChange={e => set('md', e.target.value)}
            />
          </Grid>

          {suggest('Site', 'site_name', sites.data, 4)}
          {suggest('Section', 'section', sections.data, 4)}
          {suggest('Location', 'location', locations.data, 4)}

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              label="Installation date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.installation_date}
              onChange={e => set('installation_date', e.target.value)}
              helperText="Age is calculated from this"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              select
              label="Condition"
              fullWidth
              value={form.color_category}
              onChange={e => set('color_category', e.target.value)}
            >
              <MenuItem value="">Not set</MenuItem>
              {Object.entries(COLOR_LABEL).map(([code, label]) => (
                <MenuItem key={code} value={code}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              select
              label="In service"
              fullWidth
              value={form.obsolete}
              onChange={e => set('obsolete', e.target.value as 'Yes' | 'No')}
            >
              <MenuItem value="No">Yes — still in service</MenuItem>
              <MenuItem value="Yes">No — obsolete</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              label="PO number"
              fullWidth
              value={form.po_no}
              onChange={e => set('po_no', e.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant="overline" color="text.secondary">
              Quality control
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              label="QC performed by"
              fullWidth
              value={form.qc_performed_by}
              onChange={e => set('qc_performed_by', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              label="Last QC date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.last_qc_date}
              onChange={e => setLastDate('qc', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              label="Next QC date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.next_qc_date}
              onChange={e => set('next_qc_date', e.target.value)}
              helperText="One year on by default — override it if needed"
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant="overline" color="text.secondary">
              Preventive maintenance
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              label="PM performed by"
              fullWidth
              value={form.pm_performed_by}
              onChange={e => set('pm_performed_by', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              label="Last PM date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.last_pm_date}
              onChange={e => setLastDate('pm', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              label="Next PM date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.next_pm_date}
              onChange={e => set('next_pm_date', e.target.value)}
              helperText="One year on by default — override it if needed"
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              label="Capital equity information"
              fullWidth
              value={form.capital_equity_information}
              onChange={e => set('capital_equity_information', e.target.value)}
            />
          </Grid>
        </Grid>

        <Stack direction="row" justifyContent="flex-end">
          <Button variant="contained" onClick={submit} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : device ? 'Save changes' : 'Add device'}
          </Button>
        </Stack>
      </Stack>
    </PageSection>
  );
}
